import { MembershipOverviewItem, OverviewItemProps } from '../MembershipOverviewItem';
import { useGlobalState } from '../../../state/state';
import { MembershipLevel, MembershipProductType } from '@ace-clublabs/common-membership-api-types';
import { MSSProductStatus } from '@lib/types';
import { getMembershipTitle } from '../../../utils/stringHelper';
import { Pages } from '../../../utils/constants';
import { useState } from 'react';
import { hasPITOrPendingChange } from '../../../utils/ruleHelper';
import { PITWarningModal } from '../../PITWarningModal/PITWarningModal';
import dayjs from 'dayjs';
import { getIsUpgrading, getPendingLevel } from '../../Notifications/buildNotifications';

export const RVMotorcycle = () => {
  const [membershipDetails] = useGlobalState('memberDetails');
  const [showPITWarningModal, setShowPITWarningModal] = useState(false);
  const hasPITPendingChange = hasPITOrPendingChange(membershipDetails);

  const { products, benefitLevels, prospectiveMemberRules, pendingProducts } = membershipDetails;

  const notEligibleForRV = prospectiveMemberRules.memberProducts?.find(
    (p) =>
      p.productType === MembershipProductType.RV &&
      p.productRules.every((rule) => rule.rule === MSSProductStatus.NOT_ALLOWED)
  );

  const shouldNotDisplayRV = notEligibleForRV;
  if (shouldNotDisplayRV) {
    return <></>;
  }

  const getRVItems = (): OverviewItemProps['items'] => {
    const currentLevel = benefitLevels?.existingLevel;

    const isUpgradingBenefitLevel = getIsUpgrading(membershipDetails);
    let membershipTitle = getMembershipTitle(membershipDetails, currentLevel);
    if (isUpgradingBenefitLevel) {
      const pendingLevelInfo = getPendingLevel(membershipDetails);
      membershipTitle = getMembershipTitle(membershipDetails, pendingLevelInfo?.pendingLevel!);
    }
    const RVProduct = products?.find((p) => p.type === MembershipProductType.RV);

    const currentMembershipLevelAllowsRV = prospectiveMemberRules.memberProducts?.find(
      (p) =>
        p.productType === MembershipProductType.RV &&
        p.productRules.find((rule) => rule.membershipLevel === currentLevel && rule.rule === MSSProductStatus.ALLOWED)
    );

    const upgradeIncludesRV = prospectiveMemberRules.memberProducts?.find(
      (p) =>
        p.productType === MembershipProductType.RV &&
        p.productRules.find((rule) => rule.membershipLevel !== currentLevel && rule.rule === MSSProductStatus.INCLUDED)
    );
    const upgradeAllowsRV = prospectiveMemberRules.memberProducts?.find(
      (p) =>
        p.productType === MembershipProductType.RV &&
        p.productRules.find((rule) => rule.membershipLevel !== currentLevel && rule.rule === MSSProductStatus.ALLOWED)
    );

    const pendingRV = pendingProducts?.find((product) => product.productType === MembershipProductType.RV);
    const pendingMembershipLevel = pendingProducts?.find((product) => product.productType in MembershipLevel);

    const hasPendingRVWithUpgrade =
      (() => {
        const pendingMembershipLevel = membershipDetails?.pendingProducts?.find(
          (product) => product.productType in MembershipLevel
        );
        if (pendingMembershipLevel) {
          const pendingLevel = pendingMembershipLevel.productType as MembershipLevel;
          return !!membershipDetails?.prospectiveMemberRules?.memberProducts
            ?.find((product) => product.productType === MembershipProductType.RV)
            ?.productRules?.find(
              (rule) => rule.membershipLevel === pendingLevel && rule.rule === MSSProductStatus.INCLUDED
            );
        }
        return false;
      })() || false;

    switch (true) {
      case !!pendingRV:
        return [
          {
            type: 'info',
            title: 'Roadside Assistance for RV/motorcycle has been added to your membership',
            subtitle: `Effective on: ${dayjs(pendingRV?.effectiveDate).format('MM/DD/YYYY')}`,
            key: 'rv-pending-description',
            sx: { '#rv-pending-description': { color: '#0C7A93' } },
          },
        ];

      case RVProduct && RVProduct.status === MSSProductStatus.EXISTING:
        return [
          {
            type: 'description',
            description: 'Roadside Assistance for RV/motorcycle has been added to your membership',
            key: 'rv-added-description',
          },
        ];

      case RVProduct && RVProduct.status === MSSProductStatus.INCLUDED:
        return [
          {
            type: 'description',
            description: (
              <>Roadside Assistance for RV/motorcycle is included as part of your {membershipTitle} membership</>
            ),
            key: 'rv-included-description',
          },
        ];

      case hasPendingRVWithUpgrade:
        return [
          {
            type: 'info',
            title: <>Roadside Assistance for RV/motorcycle is included as part of your {membershipTitle} membership</>,
            subtitle: `Effective on: ${dayjs(pendingMembershipLevel?.effectiveDate).format('MM/DD/YYYY')}`,
            key: 'rv-included-description',
          },
        ];

      case !RVProduct && currentMembershipLevelAllowsRV != undefined && !hasPITPendingChange:
        return [
          {
            type: 'link',
            text: 'Add roadside for RV/motorcycle',
            link: Pages.AddRVMotorcycle,
            key: 'add-rv-link',
          },
        ];

      case !RVProduct && currentMembershipLevelAllowsRV != undefined && hasPITPendingChange:
        return [
          {
            type: 'link',
            text: 'Add roadside for RV/motorcycle',
            link: Pages.AddRVMotorcycle,
            key: 'add-rv-link',
            onClick: () => {
              setShowPITWarningModal(true);
            },
          },
        ];

      case !RVProduct && (upgradeIncludesRV != undefined || upgradeAllowsRV != undefined) && !hasPITPendingChange:
        return [
          {
            type: 'description',
            description: 'Upgrade your benefit level to add Roadside Assistance for RV/motorcycle',
            key: 'upgrade-rv-description',
          },
          {
            type: 'link',
            text: 'Upgrade benefit',
            link: Pages.ChangeBenefitLevelPage,
            key: 'upgrade-benefit-link',
          },
        ];

      case !RVProduct && (upgradeIncludesRV != undefined || upgradeAllowsRV != undefined) && hasPITPendingChange:
        return [
          {
            type: 'description',
            description: 'Upgrade your benefit level to add Roadside Assistance for RV/motorcycle',
            key: 'upgrade-rv-description',
          },
          {
            type: 'link',
            text: 'Upgrade benefit',
            link: Pages.ChangeBenefitLevelPage,
            key: 'upgrade-benefit-link',
            onClick: () => {
              setShowPITWarningModal(true);
            },
          },
        ];

      default:
        return [];
    }
  };

  const RVItems: OverviewItemProps['items'] = getRVItems();

  return (
    <>
      <MembershipOverviewItem
        id="rv-motorcycle-overview"
        iconName="van-shuttle-duotone"
        overviewText="RV/motorcycle"
        items={RVItems}
      />
      <PITWarningModal
        visible={showPITWarningModal}
        onClose={() => {
          setShowPITWarningModal(false);
        }}
      />
    </>
  );
};

/**
 * CASE 1: Add-on

If membership does not have RVM and its available as an add on in the market then display “Add roadside for RV/motorcycle >” CTA (the redirect link will be handled in a separate story)
- products array NOT contain type == 'RV' && prospectiveMemberRules.memberProducts productType == 'RV' benefitLevels.existingLevel === productRules[benefitLevels.existingLevel] is ALLOWED

If membership does have RVM as a separate product then display “Roadside Assistance for RV/motorcycle has been added to your membership” 
- products array -> type == 'RV' && status == 'Existing'

CASE 2: Included on membership with Premier or Plus upgrade (TX/NM)

If membership benefit level includes RVM coverage then display content message that reads “Roadside Assistance for RV/motorcycle is included as part of your <Premier/Plus> membership” 
- products array -> type == 'RV' && status == 'Included'

If membership is classic membership and they want to add RVM then display content message “Upgrade your benefit level to add Roadside Assistance for RV/motorcycle” and display “Upgrade benefit >” CTA (the redirect link will be handled in a separate story)
- benefitLevels.existingLevel == CLASSIC && prospectiveMemberRules.memberProducts productType == 'RV' && membershipLevel: 'PLUS', rule: 'INCLUDED' }, { membershipLevel: 'PREMIER', rule: 'INCLUDED' },

CASE 3: Add on is available AFTER membership is upgraded to Plus or Premier

If membership benefit has been upgraded to Plus or Premier and RVM coverage is not added to the membership but it’s available to be added  then display “Add roadside for RV/motorcycle >” CTA (the redirect link will be handled in a separate story)
- products array NOT contain type == 'RV' && prospectiveMemberRules.memberProducts productType == 'RV' && membershipLevel: 'PLUS' (map benefitLevels.existingLevel), rule: 'ALLOWED' }, { membershipLevel: 'PREMIER' (map benefitLevels.existingLevel), rule: 'ALLOWED' },
 */
